/**
 * SunoSourceTrackPin — панель закреплённого source-трека для операций
 * над треком (extend / cover / add_vocals / add_instrumental).
 *
 * Содержит:
 *   - Обложку трека (fallback — иконка аудио)
 *   - Название + жанр
 *   - Мини аудио-плеер с play/pause и прогресс-слайдером
 *   - Для `extend`: слайдер «С какой секунды продолжить» — синхронизируется
 *     с прогрессом плеера, двусторонне. Юзер слушает, ставит ползунок
 *     на нужной секунде → значение уходит в `settings.audioContinueAt`.
 *   - Hint что делать дальше — «Нажми "Создать" внизу чтобы продлить»
 */
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { Icon } from '../../../../public/icons/Icon.tsx'
import { useGenerate } from '../../../context/GenerateContext'
import { Waveform } from '../../../components/MediaPlayer/Waveform'
import TokenPrice from '../../../components/TokenPrice'
import SpotlightTour from '../../../components/SpotlightTour'
import {
	buildPinTourSteps,
	SUNO_PIN_TOUR_KEYS,
	type SunoPinOperation,
} from '../../../data/widgetTourSteps'

// Табы переключения операций. Важен порядок — слева направо от «самой
// простой модификации» (продолжить — без сильных изменений) до «самой
// крупной» (заменить инструментал / вокал целиком).
// `variantKey` — ключ variant'а у Suno-модели для поиска цены (совпадает с
// маппингом в useGenerateCost: cover → upload_cover).
const OPERATION_TABS: {
	key: 'extend' | 'cover' | 'add_vocals' | 'add_instrumental'
	label: string
	short: string
	variantKey: string
}[] = [
	{ key: 'extend', label: 'Продлить', short: 'Продлить', variantKey: 'extend' },
	{ key: 'cover', label: 'Cover', short: 'Cover', variantKey: 'upload_cover' },
	{ key: 'add_vocals', label: 'Добавить вокал', short: '+ Вокал', variantKey: 'add_vocals' },
	{ key: 'add_instrumental', label: 'Добавить инструментал', short: '+ Инстр.', variantKey: 'add_instrumental' },
]

const OPERATION_LABELS: Record<string, { title: string; hint: string; cta: string }> = {
	extend: {
		title: 'Продолжаем трек',
		hint: 'Suno обрежет трек на выбранной секунде (жёлтая метка) и допишет продолжение. Текст песни и стиль ниже — это слова и жанр для НОВОЙ части, оригинальное начало останется как есть.',
		cta: 'продлить трек',
	},
	cover: {
		title: 'Cover',
		hint: 'Suno перепоёт ВЕСЬ трек в другом стиле. Ритм/настроение вдохновляются оригиналом, но мелодия и аранжировка будут новыми. Меняй стиль, вокал и промпт ниже — что укажешь, то и получится.',
		cta: 'создать Cover-версию',
	},
	add_vocals: {
		title: 'Добавляем вокал',
		hint: 'К инструменталу наложится новый голос. Исходная мелодия/ритм сохранятся, добавится вокальная партия. Укажи текст песни (или оставь — Suno сам напишет под стиль), выбери пол голоса и жанр — что укажешь, то и получится.',
		cta: 'добавить вокал',
	},
	add_instrumental: {
		title: 'Добавляем инструментал',
		hint: 'К вокальной дорожке добавится аккомпанемент. Вокал останется оригинальным, добавится новая музыка. Опиши нужный инструментал в поле «Стиль» ниже (например «acoustic guitar, piano, soft strings»).',
		cta: 'добавить инструментал',
	},
}

function formatTime(s: number): string {
	if (!Number.isFinite(s) || s < 0) return '0:00'
	const mm = Math.floor(s / 60)
	const ss = Math.floor(s % 60)
	return `${mm}:${ss.toString().padStart(2, '0')}`
}

export default function SunoSourceTrackPin() {
	const { sourceTrack, audioOperation, setSourceTrack, settings, updateSetting, prompt, selectedModel, isLoading } =
		useGenerate()

	// Провайдер исходного трека — лейблы должны называть его (Mureka-трек ≠ «Suno допишет»).
	const providerName = selectedModel?.id?.startsWith('mureka') ? 'Mureka' : 'Suno'

	// Цена для таба — ищем variant с нужным ключом в выбранной Suno-модели.
	// Если variant не задан в БД — null, тогда на табе цену не покажем
	// (не хотим показывать «падение на базовую» 30 на каждом табе — это
	// мисклюшн, юзер подумает что всё стоит одинаково).
	const priceForOperation = (variantKey: string): number | null => {
		const v = selectedModel?.variants?.find((x: { key: string; credits_price?: number | null }) => x.key === variantKey)
		return v?.credits_price ?? null
	}

	const audioRef = useRef<HTMLAudioElement>(null)
	const pinRef = useRef<HTMLDivElement>(null)
	const [currentTime, setCurrentTime] = useState(0)
	const [isPlaying, setIsPlaying] = useState(false)
	const [duration, setDuration] = useState(0)
	// Genie-эффект: при старте extend/cover/... generation pin «улетает»
	// в audio result-slot. Сразу после анимации pin остаётся в DOM
	// (sourceTrack ещё не null — снимется при onGenerationSuccess),
	// но визуально исчезает. Повторное открытие pin'а = новый mount.
	const [isFlying, setIsFlying] = useState(false)

	// Per-operation tour state. Ключ для каждой из 4 параметрических
	// операций свой, поэтому тур открывается отдельно для каждой —
	// пройдя Extend-тур юзер всё ещё увидит Cover-тур при переключении.
	const [tourOp, setTourOp] = useState<SunoPinOperation | null>(null)

	// Читаем текущее значение continueAt (или 0 если не установлено).
	const continueAt = settings.audioContinueAt ?? 0
	const maxDuration = sourceTrack?.duration ?? duration
	// Ref для свежего continueAt в onTime-замыкании — useEffect ниже
	// подписывается один раз на `sourceTrack.id`, поэтому без ref'а
	// чтение settings.audioContinueAt в onTime было бы stale.
	const continueAtRef = useRef<number | undefined>(settings.audioContinueAt)
	useEffect(() => {
		continueAtRef.current = settings.audioContinueAt
	}, [settings.audioContinueAt])

	// Когда трек меняется — сбрасываем плеер и state.
	useEffect(() => {
		setCurrentTime(0)
		setIsPlaying(false)
		setIsFlying(false)
		if (audioRef.current) {
			audioRef.current.pause()
			audioRef.current.currentTime = 0
		}
	}, [sourceTrack?.id])

	// Генерация закончилась (успех ИЛИ ошибка) → вернуть «улетевший» (genie) pin.
	// На успехе sourceTrack обнулится и pin размонтируется; на ОШИБКЕ sourceTrack
	// остаётся — id не меняется, reset-эффект выше не срабатывает, и без этого pin
	// навсегда невидим (isFlying завис true): юзер не видит трек и не может повторить.
	useEffect(() => {
		if (!isLoading) setIsFlying(false)
	}, [isLoading])

	// Genie-анимация «pin улетает в result-slot».
	// Срабатывает на event 'suno-extend-fly' от handleGenerate.
	//
	// КЛЮЧЕВАЯ ДЕТАЛЬ: анимируется КЛОН pin'а в document.body как fixed
	// оверлей. Оригинал просто скрывается (isFlying=true). Это спасает
	// от мерцания — React во время генерации ре-рендерит родителя
	// виджета (setIsLoading/setResult/etc), что разрушало composite
	// layer анимации. Клон в body — вне React-дерева виджета, его
	// анимация идёт на composite thread без вмешательств.
	useEffect(() => {
		const handler = () => {
			const pin = pinRef.current
			if (!pin) return
			const target =
				document.querySelector<HTMLElement>('.gw__result-slot__audio-box') ||
				document.querySelector<HTMLElement>('.gw__audio-cards') ||
				document.querySelector<HTMLElement>('.gw__result-slot')
			if (!target) return

			const pinRect = pin.getBoundingClientRect()
			const targetRect = target.getBoundingClientRect()
			const dx =
				targetRect.left + targetRect.width / 2 - (pinRect.left + pinRect.width / 2)
			const dy =
				targetRect.top + targetRect.height / 2 - (pinRect.top + pinRect.height / 2)

			// Пауза аудио — летящий pin не должен продолжать играть.
			audioRef.current?.pause()

			// Клонируем pin со всеми стилями и вставляем в body как fixed
			const clone = pin.cloneNode(true) as HTMLElement
			// Убираем интерактивные элементы у клона — он чисто декоративный
			clone.querySelectorAll('button, input, audio').forEach((el) => {
				el.setAttribute('tabindex', '-1')
				;(el as HTMLElement).style.pointerEvents = 'none'
			})
			clone.style.position = 'fixed'
			clone.style.left = `${pinRect.left}px`
			clone.style.top = `${pinRect.top}px`
			clone.style.width = `${pinRect.width}px`
			clone.style.height = `${pinRect.height}px`
			clone.style.margin = '0'
			clone.style.zIndex = '10000'
			clone.style.pointerEvents = 'none'
			clone.style.setProperty('--genie-x', `${Math.round(dx)}px`)
			clone.style.setProperty('--genie-y', `${Math.round(dy)}px`)
			clone.classList.add('suno-source-pin--flying')
			document.body.appendChild(clone)

			// Оригинал прячем (не анимируем — клон делает это за него)
			setIsFlying(true)

			// По окончании анимации убираем клон из DOM.
			// Длительность keyframes — 680ms, добавляем буфер.
			const cleanup = () => {
				clone.remove()
				clone.removeEventListener('animationend', cleanup)
			}
			clone.addEventListener('animationend', cleanup)
			// Fallback — если animationend не сработал по любой причине
			setTimeout(cleanup, 1000)
		}
		window.addEventListener('suno-extend-fly', handler)
		return () => window.removeEventListener('suno-extend-fly', handler)
	}, [])

	// Подписка на audio events для обновления UI.
	useEffect(() => {
		const el = audioRef.current
		if (!el) return
		const onTime = () => {
			setCurrentTime(el.currentTime)
			// Пока плеер играет — continue_at СИНХРОННО следует за playback'ом.
			// Раньше был флаг manualContinueAtRef, который после drag'а слайдера
			// залипал true и блокировал sync — из-за этого маркер обрезки
			// отставал от проигрывания. Сейчас: drag слайдера тоже делает
			// seekTo → currentTime обновится → sync отработает следующим онTime.
			if (!el.paused) {
				const rounded = Math.round(el.currentTime)
				const target = rounded > 0 ? rounded : undefined
				if (target !== continueAtRef.current) {
					updateSetting('audioContinueAt', target)
				}
			}
		}
		const onPlay = () => setIsPlaying(true)
		const onPause = () => setIsPlaying(false)
		const onLoaded = () => {
			if (Number.isFinite(el.duration)) setDuration(el.duration)
		}
		el.addEventListener('timeupdate', onTime)
		el.addEventListener('play', onPlay)
		el.addEventListener('pause', onPause)
		el.addEventListener('loadedmetadata', onLoaded)
		el.addEventListener('ended', onPause)
		return () => {
			el.removeEventListener('timeupdate', onTime)
			el.removeEventListener('play', onPlay)
			el.removeEventListener('pause', onPause)
			el.removeEventListener('loadedmetadata', onLoaded)
			el.removeEventListener('ended', onPause)
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [sourceTrack?.id])

	// Авто-показ тура при первом заходе на операцию. Ключ индивидуальный
	// для каждой из 4 операций — поэтому переключение таба тоже может
	// открыть тур, если конкретно эта операция ещё не виделась юзером.
	// Небольшой delay чтобы pin закончил mount-анимацию и target'ы тура
	// успели появиться в DOM.
	useEffect(() => {
		if (!sourceTrack) return
		if (audioOperation === 'generate') return
		if (typeof window === 'undefined') return
		const op = audioOperation as SunoPinOperation
		const key = SUNO_PIN_TOUR_KEYS[op]
		if (!key) return
		if (window.localStorage.getItem(key)) return
		const t = setTimeout(() => setTourOp(op), 400)
		return () => clearTimeout(t)
	}, [audioOperation, sourceTrack])

	const handleTourFinish = useCallback(() => {
		if (tourOp) {
			try {
				window.localStorage.setItem(SUNO_PIN_TOUR_KEYS[tourOp], '1')
			} catch {
				// localStorage отключён — тур просто покажется снова
			}
		}
		setTourOp(null)
	}, [tourOp])

	const handleRestartTour = useCallback(() => {
		if (audioOperation === 'generate') return
		setTourOp(audioOperation as SunoPinOperation)
	}, [audioOperation])

	if (!sourceTrack || audioOperation === 'generate') return null

	const meta = OPERATION_LABELS[audioOperation] ?? {
		title: 'Работаем с треком',
		hint: '',
		cta: 'создать',
	}

	const trackTitle = sourceTrack.title?.trim() || 'Без названия'
	const tags = sourceTrack.tags?.trim() || ''
	const audioUrl = sourceTrack.audio_url || sourceTrack.stream_audio_url || ''
	const coverUrl = sourceTrack.image_url || ''

	const togglePlay = () => {
		const el = audioRef.current
		if (!el || !audioUrl) return
		if (el.paused) el.play().catch(() => {})
		else el.pause()
	}

	const seekTo = (seconds: number) => {
		const el = audioRef.current
		if (!el) return
		el.currentTime = seconds
		setCurrentTime(seconds)
	}

	const isExtend = audioOperation === 'extend'
	const isCover = audioOperation === 'cover'
	const isAddVocals = audioOperation === 'add_vocals'
	const isAddInstrumental = audioOperation === 'add_instrumental'

	// Для cover — проверяем что юзер реально изменил стиль.
	// Если новый style пуст ИЛИ в точности совпадает с тегами оригинала —
	// cover получится «таким же», что чаще всего не то что юзер хочет.
	const coverStyleSame = (() => {
		if (!isCover) return false
		const newStyle = (settings.audioStyle || '').trim().toLowerCase()
		const oldStyle = (sourceTrack.tags || '').trim().toLowerCase()
		if (!newStyle) return true // пусто = не указан
		return newStyle === oldStyle
	})()

	// Для add_instrumental / add_vocals — проверяем что стиль указан.
	// Если пусто → Suno ничего не сможет сгенерировать осмысленного.
	const styleEmpty = (settings.audioStyle || '').trim().length === 0

	// Является ли source трек инструменталом — по наличию lyrics.
	// Для add_vocals/add_instrumental это критично: add-vocals ждёт инстр,
	// add-instrumental — трек с вокалом. Неподходящий source → Suno отклонит
	// или получится странный результат.
	const sourceHasLyrics = !!(sourceTrack.lyrics && sourceTrack.lyrics.trim().length > 0)

	// Для add_vocals — что будет с текстом нового вокала.
	// В оригинальном Suno «Add Vocals» накладывает новую вокальную дорожку
	// (с новыми словами) поверх инструментала. `prompt` = TEXT SONG LYRICS,
	// которые новый голос пропоёт. Это не «описание что сгенерировать» —
	// Suno буквально споёт то что написано в prompt, строка за строкой.
	const addVocalsTextPlan: { mode: 'user' | 'auto'; text: string } | null = (() => {
		if (!isAddVocals) return null
		const userPrompt = (prompt || '').trim()
		if (userPrompt) {
			return {
				mode: 'user',
				text: '📝 Новый голос споёт твой текст построчно. Пиши слова песни в поле «Опишите» ниже.',
			}
		}
		// Mureka поёт ТОЛЬКО присланную лирику: без текста их API отвечает
		// «The lyrics are empty» (живой прогон 2026-08-20) — обещание «придумает
		// сам» было враньём. У Suno авто-режим остаётся как был.
		if (providerName === 'Mureka') {
			return {
				mode: 'auto',
				text: '🎤 Впиши слова песни в поле «Опишите» ниже — Mureka споёт ровно их. Без текста добавление вокала не запустится.',
			}
		}
		return {
			mode: 'auto',
			text: `🎤 В поле «Опишите» ниже — слова песни которые споёт новый голос. Оставь пусто — ${providerName} придумает слова сам под твой жанр.`,
		}
	})()

	// Для cover — определяем что будет с текстом песни.
	// Это отражает логику в generationRunners.ts — должно совпадать 1:1.
	const coverLyricsPlan: {
		mode: 'original' | 'user' | 'auto' | 'instrumental'
		text: ReactNode
	} | null = (() => {
		if (!isCover) return null
		const userPrompt = (prompt || '').trim()
		const originalLyrics = (sourceTrack.lyrics || '').trim()
		const wantsInstrumental = !!settings.audioInstrumental
		const userCustomMode = settings.audioCustomMode ?? true
		if (wantsInstrumental) {
			if (originalLyrics) {
				return {
					mode: 'instrumental',
					text: (
						<>
							🎶 Получишь инструментальную версию в новом жанре —{' '}
							<b>оригинальный вокал будет отброшен</b>. Если нужен именно
							вокал оригинала без инструментала — используй «Стемы» в шторке
							«Работать с треком».
						</>
					),
				}
			}
			return {
				mode: 'instrumental',
				text: '🎶 Инструментальная версия без вокала в новом жанре',
			}
		}
		if (userPrompt) {
			return { mode: 'user', text: '📝 Используется твой текст (ниже в «Опишите»)' }
		}
		if (!userCustomMode) {
			// Явно указываем причину — это выбор юзера (Кастомный выкл).
			// Если у оригинала есть текст, но custom_mode=off, юзер может
			// захотеть его сохранить → подсказываем как.
			if (originalLyrics) {
				return {
					mode: 'auto',
					text: (
						<>
							🎤 {providerName} напишет новый текст под жанр —{' '}
							<b>«Кастомный» ниже выключен</b>. Включи, чтобы сохранить текст
							оригинала.
						</>
					),
				}
			}
			return { mode: 'auto', text: `🎤 ${providerName} напишет новый текст под жанр («Кастомный» ниже выключен).` }
		}
		if (originalLyrics) {
			return { mode: 'original', text: '✓ Текст оригинала сохранится, сменится только стиль' }
		}
		return { mode: 'auto', text: `🎤 ${providerName} напишет новый текст под жанр (у оригинала текста нет).` }
	})()

	return (
		<>
		<div
			ref={pinRef}
			className={`suno-source-pin${isFlying ? ' suno-source-pin--flying' : ''}`}
			aria-hidden={isFlying}
		>
			{/* ── Header: компактный — бейдж + название + жанр в одной строке ── */}
			<div className="suno-source-pin__row">
				<div className="suno-source-pin__text">
					<span className="suno-source-pin__badge">{meta.title}</span>
					<span className="suno-source-pin__track" title={trackTitle}>
						{trackTitle}
					</span>
					{tags && (
						<span className="suno-source-pin__tags" title={tags}>
							· {tags}
						</span>
					)}
				</div>

				<button
					type="button"
					className="suno-source-pin__help"
					onClick={handleRestartTour}
					aria-label="Как работает эта операция"
					title="Как работает эта операция"
				>
					?
				</button>

				<button
					className="suno-source-pin__close"
					onClick={() => setSourceTrack(null)}
					type="button"
					aria-label="Отменить — вернуться к обычной генерации"
					title="Отменить"
				>
					<Icon id="gen-close" size={10} />
				</button>
			</div>

			{/* ── Табы операций: переключение без смены source-трека ──── */}
			<nav className="suno-source-pin__ops-tabs" role="tablist">
				{OPERATION_TABS.map((tab) => {
					const active = tab.key === audioOperation
					const price = priceForOperation(tab.variantKey)
					return (
						<button
							key={tab.key}
							type="button"
							role="tab"
							aria-selected={active}
							className={`suno-source-pin__ops-tab${active ? ' suno-source-pin__ops-tab--active' : ''}`}
							onClick={() => setSourceTrack(sourceTrack, tab.key)}
							title={OPERATION_LABELS[tab.key]?.title ?? tab.label}
						>
							<span className="suno-source-pin__ops-tab-label">{tab.short}</span>
							{price !== null && (
								<span className="suno-source-pin__ops-tab-price">
									<TokenPrice value={price} iconSize={10} />
								</span>
							)}
						</button>
					)
				})}
			</nav>

			{/* ── Audio player: обложка-с-play + волна + cut-slider ─ */}
			{audioUrl && (
				<div className="suno-source-pin__player">
					{/* Обложка с постоянно видимой play-кнопкой — слева от волны */}
					<div className="suno-source-pin__cover">
						{coverUrl ? (
							<img
								src={coverUrl}
								alt=""
								loading="lazy"
								className="suno-source-pin__cover-img"
							/>
						) : (
							<div className="suno-source-pin__cover-placeholder" aria-hidden="true">
								<Icon id="gen-audio" size={22} />
							</div>
						)}
						<button
							type="button"
							className="suno-source-pin__cover-play"
							onClick={togglePlay}
							aria-label={isPlaying ? 'Пауза' : 'Воспроизвести'}
							title={isPlaying ? 'Пауза' : 'Воспроизвести'}
						>
							{isPlaying ? (
								<svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
									<rect x="4" y="3" width="3" height="10" rx="0.7" />
									<rect x="9" y="3" width="3" height="10" rx="0.7" />
								</svg>
							) : (
								<svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
									<path d="M4 2.5 L13 8 L4 13.5 Z" />
								</svg>
							)}
						</button>
					</div>

					{/* Центральная колонка: waveform сверху, extend-slider снизу. */}
					<div className="suno-source-pin__player-center">
						<Waveform
							src={audioUrl}
							progress={maxDuration > 0 ? currentTime / maxDuration : 0}
							cutMarker={
								isExtend && maxDuration > 0 && continueAt > 0
									? continueAt / maxDuration
									: undefined
							}
							onSeek={(ratio) => {
								const n = Math.round(ratio * (maxDuration || 0))
								seekTo(n)
								// Клик по waveform обновляет и continueAt — иначе на
								// паузе метка обрезки оставалась на старом месте.
								updateSetting('audioContinueAt', n > 0 ? n : undefined)
							}}
							height={44}
							className="suno-source-pin__waveform"
						/>

						{isExtend && (
							<div className="suno-source-pin__cut-slider-wrap">
								<input
									type="range"
									className="suno-source-pin__cut-slider"
									min={0}
									max={maxDuration || 0}
									step={1}
									value={continueAt}
									onChange={(e) => {
										const n = Number(e.target.value)
										const target =
											Number.isFinite(n) && n > 0 ? n : undefined
										updateSetting('audioContinueAt', target)
										// Прокручиваем плеер на эту позицию — сразу слышно.
										seekTo(n)
									}}
									aria-label="Секунда с которой продолжить"
									title={`Секунда обрезки: ${formatTime(continueAt)}`}
								/>
								{/* Overlay-треугольник: позиционируется строго по
								    ratio (0..100%), без thumb-width offset нативного
								    range-а. Pointer-events:none — hit-area держит
								    невидимый native thumb. */}
								<span
									className="suno-source-pin__cut-thumb"
									style={{
										left:
											maxDuration > 0
												? `${(continueAt / maxDuration) * 100}%`
												: '0%',
									}}
									aria-hidden="true"
								/>
							</div>
						)}
					</div>

					<div className="suno-source-pin__time-col">
						{/* Первая строка высотой 44px — на уровне центра Waveform'а */}
						<div className="suno-source-pin__time-row suno-source-pin__time-row--waveform">
							<span className="suno-source-pin__time">
								{formatTime(currentTime)} / {formatTime(maxDuration)}
							</span>
						</div>
						{/* Вторая строка высотой 16px — на уровне cut-slider'а */}
						{isExtend && (
							<div className="suno-source-pin__time-row suno-source-pin__time-row--slider">
								<span className="suno-source-pin__cut-badge" title="Секунда обрезки">
									<svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true">
										<circle cx="2.5" cy="7.5" r="1.5" stroke="currentColor" strokeWidth="1" />
										<circle cx="7.5" cy="7.5" r="1.5" stroke="currentColor" strokeWidth="1" />
										<path d="M3.5 6 L8 2 M6.5 6 L2 2" stroke="currentColor" strokeWidth="1" strokeLinecap="round" />
									</svg>
									{continueAt > 0 ? formatTime(continueAt) : 'с конца'}
								</span>
							</div>
						)}
					</div>

					<audio
						ref={audioRef}
						src={audioUrl}
						preload="metadata"
						style={{ display: 'none' }}
					/>
				</div>
			)}

			{/* ── Динамический блок (зависит от audioOperation) ─────
			    `key={audioOperation}` пересоздаёт блок при смене операции →
			    CSS-анимация fade+slide срабатывает на mount. Player и header
			    выше НЕ пересоздаются — audio/waveform не прерываются. */}
			<div
				className="suno-source-pin__operation-content"
				key={audioOperation}
			>
			{/* ── Cover: компактная строка «Jazz → {новый}» + план текста ── */}
			{isCover && (
				<>
					<div className="suno-source-pin__diff">
						<span className="suno-source-pin__diff-old" title={sourceTrack.tags || ''}>
							{sourceTrack.tags?.trim() || '—'}
						</span>
						<span className="suno-source-pin__diff-arrow" aria-hidden="true">→</span>
						<span
							className={`suno-source-pin__diff-new${coverStyleSame ? ' suno-source-pin__diff-new--warning' : ''}`}
							title={settings.audioStyle || ''}
						>
							{settings.audioStyle?.trim() || '(укажи стиль ниже)'}
						</span>
						{coverStyleSame && (
							<span className="suno-source-pin__diff-warn">
								⚠ Стиль такой же — cover получится идентичным оригиналу
							</span>
						)}
					</div>
					{coverLyricsPlan && (
						<div
							className={`suno-source-pin__plan suno-source-pin__plan--${coverLyricsPlan.mode}`}
						>
							{coverLyricsPlan.text}
						</div>
					)}
				</>
			)}

			{/* ── +Вокал: diff + план текста + warning про source ── */}
			{isAddVocals && (
				<>
					<div className="suno-source-pin__diff">
						<span className="suno-source-pin__diff-old">
							{sourceHasLyrics ? 'трек с вокалом' : 'инструментал'}
						</span>
						<span className="suno-source-pin__diff-arrow" aria-hidden="true">→</span>
						<span className="suno-source-pin__diff-new">
							+ новый вокал
							{settings.audioVocalGender === 'm'
								? ' (муж)'
								: settings.audioVocalGender === 'f'
									? ' (жен)'
									: ''}
						</span>
						{providerName === 'Mureka' && !(prompt || '').trim() ? (
							<span className="suno-source-pin__diff-warn">
								⚠ Впиши текст песни — Mureka без лирики не запускает добавление вокала
							</span>
						) : styleEmpty && !prompt ? (
							<span className="suno-source-pin__diff-warn">
								⚠ Укажи текст или стиль — иначе {providerName} не знает что петь
							</span>
						) : null}
					</div>

					{/* План нового текста */}
					{addVocalsTextPlan && (
						<div className={`suno-source-pin__plan suno-source-pin__plan--${addVocalsTextPlan.mode}`}>
							{addVocalsTextPlan.text}
						</div>
					)}

					{/* Warning если source — трек с вокалом. Suno API add-vocals
					    работает ТОЛЬКО с инструменталом — на треке с голосом
					    Suno либо отклонит запрос, либо выдаст кашу с двумя
					    вокалами. Поэтому предлагаем правильную операцию
					    (Cover) или путь через Стемы. */}
					{sourceHasLyrics && (
						<div className="suno-source-pin__plan suno-source-pin__plan--error">
							<strong>Этот трек с вокалом — «+ Вокал» не подойдёт.</strong>
							<span className="suno-source-pin__plan-desc">
								В {providerName} операция «Add Vocals» работает только на
								инструментальных треках (накладывает новый голос
								поверх чистой музыки). На треке с голосом запрос
								будет отклонён.
							</span>
							<div className="suno-source-pin__plan-actions">
								<button
									type="button"
									className="suno-source-pin__plan-cta"
									onClick={() => setSourceTrack(sourceTrack, 'cover')}
								>
									→ Cover
								</button>
								<span className="suno-source-pin__plan-cta-hint">
									меняет стиль ВСЕГО трека (вокал + музыка)
								</span>
							</div>
							<div className="suno-source-pin__plan-alt">
								Или выдели инструментал через «Стемы» (в меню
								«Улучшить» у трека) и применяй «+ Вокал» уже к нему.
							</div>
						</div>
					)}
				</>
			)}

			{/* ── +Инструментал: diff + warning если source уже инструментал ── */}
			{isAddInstrumental && (
				<>
					<div className="suno-source-pin__diff">
						<span className="suno-source-pin__diff-old">
							{sourceHasLyrics ? 'вокал без аккомпанемента' : 'трек (состав не определён)'}
						</span>
						<span className="suno-source-pin__diff-arrow" aria-hidden="true">→</span>
						<span
							className={`suno-source-pin__diff-new${styleEmpty ? ' suno-source-pin__diff-new--warning' : ''}`}
						>
							{styleEmpty ? '+ инструментал (укажи жанр ниже)' : `+ ${settings.audioStyle}`}
						</span>
						{styleEmpty && (
							<span className="suno-source-pin__diff-warn">
								⚠ Опиши инструментал в поле «Стиль» (например «acoustic guitar»)
							</span>
						)}
					</div>

					{!sourceHasLyrics && (
						<div className="suno-source-pin__plan suno-source-pin__plan--warning">
							⚠ У трека не виден текст песни — возможно, это уже инструментал.
							«+ Инструментал» добавляет музыку к <b>вокальной</b> дорожке.
							Если в треке нет вокала — операция ничего не изменит или вернёт
							ошибку. Выдели вокал через «Стемы» если трек микс.
						</div>
					)}
				</>
			)}

			{/* ── Extend: легенда «что останется / что заменится» ─ */}
			{isExtend && audioUrl && (
				<div className="suno-source-pin__legend">
					<span className="suno-source-pin__legend-item suno-source-pin__legend-item--keep">
						<span className="suno-source-pin__legend-swatch suno-source-pin__legend-swatch--keep" />
						До {formatTime(continueAt || maxDuration)} — <b>твой оригинал</b>
					</span>
					<span className="suno-source-pin__legend-item suno-source-pin__legend-item--replace">
						<span className="suno-source-pin__legend-swatch suno-source-pin__legend-swatch--replace" />
						{continueAt > 0
							? <>После — <b>{providerName} допишет</b> с твоими текстом/стилем</>
							: <>{providerName} допишет продолжение <b>после конца трека</b></>}
					</span>
					{continueAt > 0 && (
						<button
							type="button"
							className="suno-source-pin__cut-reset"
							onClick={() => {
								// Сначала пауза — иначе onTime сразу же пересинкнет
								// continueAt с currentTime, и «reset» визуально не
								// сработает. Заодно перематываем плеер в 0.
								const el = audioRef.current
								if (el) {
									el.pause()
									el.currentTime = 0
								}
								setCurrentTime(0)
								updateSetting('audioContinueAt', undefined)
							}}
							title="Убрать метку обрезки и продолжить с конца трека"
						>
							⟲ сбросить метку
						</button>
					)}
				</div>
			)}

			{/* ── Footer: только CTA-строка. Полный hint — в tooltip ─── */}
			<div className="suno-source-pin__footer">
				<span
					className="suno-source-pin__hint-icon"
					title={meta.hint.replace(/Suno/g, providerName)}
					aria-label={meta.hint.replace(/Suno/g, providerName)}
				>
					ⓘ
				</span>
				<span className="suno-source-pin__cta">
					Нажми <kbd>Создать</kbd> внизу чтобы {meta.cta}
				</span>
			</div>
			</div>
		</div>

		{/* Per-operation тур. Portal → document.body — тот же паттерн что
		    в SunoStyleSheet/LyricsSheet: SpotlightTour position:fixed
		    может прилипнуть к containing block'у родителя (widget-grid
		    с transform), уйдёт за виджет. Portal в body гарантирует что
		    rect относительно viewport'а и z-index 10100+ над всем. */}
		{tourOp && createPortal(
			<SpotlightTour
				steps={buildPinTourSteps(tourOp)}
				storageKey={SUNO_PIN_TOUR_KEYS[tourOp]}
				onFinish={handleTourFinish}
			/>,
			document.body,
		)}
		</>
	)
}
